import {ShopProduct} from './product';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {useCartStore} from './use-cart-store';
import {useState} from 'react';
import {CheckCircleIcon} from '@ui/icons/material/CheckCircle';
import {ShoppingCartIcon} from '@ui/icons/material/ShoppingCart';

interface ProductCardProps {
  product: ShopProduct;
  localCurrency?: string;
  localCurrencyRate?: number;
}

export function ProductCard({product, localCurrency = "GBP", localCurrencyRate = 1}: ProductCardProps) {
  const addToCart = useCartStore(state => state.addToCart);
  const [showAdded, setShowAdded] = useState(false);
  const handleAddToCart = () => {
    
    addToCart(product);
    setShowAdded(true);
    setTimeout(() => {
      setShowAdded(false);
    }, 2000);
  };

  // Use product's localCurrency if available, otherwise use prop
  const displayCurrency = product.localCurrency || localCurrency;
  // localCurrencyRate is already the converted price in local currency
  const displayPrice = product.localCurrencyRate || parseFloat(product.price[0]?.price || '0');
  
  // Currency symbol mapping
  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'GBP': return '£';
      case 'USD': return '$';
      case 'INR': return '₹';
      default: return currency;
    }
  };

  return (
    <div className="p-12 md:p-16 rounded-lg border border-divider hover:shadow-md transition-shadow bg-surface">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-12 md:gap-16">
        {/* Product Info */}
        <div className="flex items-center gap-12 flex-1 w-full sm:w-auto">
          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              className="w-48 h-48 md:w-60 md:h-60 object-contain rounded flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-semibold mb-4">{product.name}</h3>
            {product.storage && (
              <p className="text-xs md:text-sm text-muted">
                {product.storage} GB <Trans message="Cloud Storage" />
              </p>
            )}
            {product.description && (
              <p className="text-xs md:text-sm text-muted mt-4 line-clamp-2">{product.description}</p>
            )}
          </div>
        </div>

        {/* Price and Add to Cart */}
        <div className="flex items-center justify-between sm:justify-end gap-12 w-full sm:w-auto sm:flex-col sm:items-end sm:gap-8">
          <div className="text-left sm:text-right">
            <div className="text-base md:text-lg font-bold">
              {getCurrencySymbol(displayCurrency)}{displayPrice.toFixed(2)}
            </div>
          </div>
          <Button
            variant={showAdded ? 'flat' : 'outline'}
            color={showAdded ? 'positive' : 'primary'}
            size="xs"
            onClick={handleAddToCart}
            startIcon={showAdded ? <CheckCircleIcon /> : <ShoppingCartIcon />}
            className="whitespace-nowrap w-auto sm:w-auto"
          >
            {showAdded ? (
              <Trans message="Added" />
            ) : (
              <Trans message="Add to Cart" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
